'use client';

import { useCallback } from 'react';
import { apiClient } from '@/lib/apiClient';
import { useCachedQuery } from '@/api/queryCache';
import type { InviteCodeResponse, PartnerConventionResponse, RegisterInvitePayload } from './types';

/**
 * Chi invita e che cosa offre. Rotta pubblica (30 richieste/min per IP): un
 * errore qualsiasi — codice sconosciuto, throttle, rete — per la pagina vale
 * «invito generico», quindi non lo distinguiamo.
 */
export function useInviteCode(code: string | null) {
  const fetcher = useCallback(
    () =>
      apiClient<never, InviteCodeResponse>(`/v1/invite-codes/${encodeURIComponent(code ?? '')}`, {
        method: 'GET',
      }),
    [code]
  );
  return useCachedQuery(code ? `invite-code:${code}` : null, fetcher);
}

/** La convenzione dalla pagina partner: stesso trattamento degli errori del lookup per codice. */
export function usePartnerConvention(slug: string | null) {
  const fetcher = useCallback(
    () =>
      apiClient<never, PartnerConventionResponse>(
        `/v1/invite-codes/partners/${encodeURIComponent(slug ?? '')}`,
        { method: 'GET' }
      ),
    [slug]
  );
  return useCachedQuery(slug ? `partner-convention:${slug}` : null, fetcher);
}

/**
 * Aggancia il codice alla visita già tracciata. Vince l'ultimo invito. Gli
 * errori (codice sconosciuto, visita orfana, 422, 429) si ignorano: il codice
 * viaggia comunque sull'URL di registrazione.
 */
export async function registerInvite(payload: RegisterInvitePayload): Promise<void> {
  await apiClient<RegisterInvitePayload, Record<string, never>>('/v1/landing/invite', {
    method: 'POST',
    body: payload,
  });
}
